export class DasaIndexedDb {
    private static readonly DB_NAME = 'DasaOfflineDb';
    private static readonly STORE_NAME = 'appState';
    private static readonly DB_VERSION = 1;

    /**
     * One shared connection for the life of the page.
     *
     * Every set/get used to open its own connection and close it again. With
     * putInSession writing 19 keys and loadFromSession reading 20, a single
     * save/restore cycle performed ~39 open/close handshakes — and my-orders
     * runs that cycle on every page load, filter click and websocket-driven
     * refetch. Holding one connection removes the handshakes entirely.
     */
    private static dbPromise: Promise<IDBDatabase> | null = null;

    private static openDb(): Promise<IDBDatabase> {
        if (DasaIndexedDb.dbPromise) {
            return DasaIndexedDb.dbPromise;
        }
        DasaIndexedDb.dbPromise = DasaIndexedDb.openDbInternal().catch(err => {
            // Don't cache a failed open — the next call should retry.
            DasaIndexedDb.dbPromise = null;
            throw err;
        });
        return DasaIndexedDb.dbPromise;
    }

    private static openDbInternal(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            if (!window.indexedDB) {
                reject(new Error('IndexedDB is not supported in this browser.'));
                return;
            }

            const request = window.indexedDB.open(
                DasaIndexedDb.DB_NAME,
                DasaIndexedDb.DB_VERSION
            );

            request.onupgradeneeded = function () {
                const db = request.result;
                if (!db.objectStoreNames.contains(DasaIndexedDb.STORE_NAME)) {
                    db.createObjectStore(DasaIndexedDb.STORE_NAME);
                }
            };

            request.onsuccess = function () {
                const db = request.result;
                // A cached handle that the browser (or another tab's upgrade)
                // has closed would throw InvalidStateError on every later call.
                // Drop it so the next operation opens a fresh one.
                db.onclose = () => { DasaIndexedDb.dbPromise = null; };
                db.onversionchange = () => {
                    DasaIndexedDb.dbPromise = null;
                    db.close();
                };
                resolve(db);
            };

            request.onerror = function () {
                reject(request.error || new Error('Failed to open IndexedDB.'));
            };
        });
    }

    public static async set(key: string, value: any): Promise<void> {
        const db = await DasaIndexedDb.openDb();

        return new Promise((resolve, reject) => {
            const tx = db.transaction(DasaIndexedDb.STORE_NAME, 'readwrite');
            const store = tx.objectStore(DasaIndexedDb.STORE_NAME);
            store.put(value, key);

            tx.oncomplete = function () { resolve(); };
            tx.onerror = function () {
                reject(tx.error || new Error('IndexedDB write failed.'));
            };
        });
    }

    public static async get(key: string): Promise<any> {
        const db = await DasaIndexedDb.openDb();

        return new Promise((resolve, reject) => {
            const tx = db.transaction(DasaIndexedDb.STORE_NAME, 'readonly');
            const store = tx.objectStore(DasaIndexedDb.STORE_NAME);
            const request = store.get(key);

            request.onsuccess = function () { resolve(request.result); };
            request.onerror = function () {
                reject(request.error || new Error('IndexedDB read failed.'));
            };
        });
    }

    public static async remove(key: string): Promise<void> {
        const db = await DasaIndexedDb.openDb();

        return new Promise((resolve, reject) => {
            const tx = db.transaction(DasaIndexedDb.STORE_NAME, 'readwrite');
            const store = tx.objectStore(DasaIndexedDb.STORE_NAME);
            store.delete(key);

            tx.oncomplete = function () { resolve(); };
            tx.onerror = function () {
                reject(tx.error || new Error('IndexedDB delete failed.'));
            };
        });
    }

    /**
     * Write many keys in ONE transaction.
     *
     * Replaces 19 separate transactions with a single one, and skips any key
     * whose value is the same object reference we last wrote — the vendor app
     * re-persists the whole app state on every orders refresh, and most of it
     * (countries, businesses, store items) has not changed since boot.
     * Structure-cloning those unchanged blobs was pure main-thread cost.
     */
    private static lastWritten = new Map<string, any>();

    public static async setMany(entries: Record<string, any>): Promise<void> {
        const keys = Object.keys(entries).filter(
            k => DasaIndexedDb.lastWritten.get(k) !== entries[k]
        );
        if (!keys.length) { return; }

        const db = await DasaIndexedDb.openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(DasaIndexedDb.STORE_NAME, 'readwrite');
            const store = tx.objectStore(DasaIndexedDb.STORE_NAME);
            for (const k of keys) {
                store.put(entries[k], k);
            }
            tx.oncomplete = function () {
                for (const k of keys) { DasaIndexedDb.lastWritten.set(k, entries[k]); }
                resolve();
            };
            tx.onerror = function () {
                reject(tx.error || new Error('IndexedDB batch write failed.'));
            };
        });
    }

    /** Read many keys in ONE transaction, returned in the order requested. */
    public static async getMany(keys: string[]): Promise<any[]> {
        const db = await DasaIndexedDb.openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(DasaIndexedDb.STORE_NAME, 'readonly');
            const store = tx.objectStore(DasaIndexedDb.STORE_NAME);
            const out: any[] = new Array(keys.length);
            keys.forEach((k, i) => {
                const req = store.get(k);
                req.onsuccess = () => { out[i] = req.result; };
            });
            tx.oncomplete = function () { resolve(out); };
            tx.onerror = function () {
                reject(tx.error || new Error('IndexedDB batch read failed.'));
            };
        });
    }

    public static async clear(): Promise<void> {
        const db = await DasaIndexedDb.openDb();

        return new Promise((resolve, reject) => {
            const tx = db.transaction(DasaIndexedDb.STORE_NAME, 'readwrite');
            const store = tx.objectStore(DasaIndexedDb.STORE_NAME);
            store.clear();

            tx.oncomplete = function () {
                // The skip-unchanged cache is now stale — everything is gone.
                DasaIndexedDb.lastWritten.clear();
                resolve();
            };
            tx.onerror = function () {
                reject(tx.error || new Error('IndexedDB clear failed.'));
            };
        });
    }
}